import { JSONUtils } from '../../../../utility/json-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { Cardinal } from '../../../core/cardinal.js';
import { MigrationSafe } from '../../../core/migration-safe.js';
import { cloneData, isSameData } from '../../../dto/data-model-utils.js';
import { SerializedData, SerializationContext, DataModelBuilder } from '../../../dto/data-model.js';
import { OpenAccessPass } from '../../open-access-pass/open-access-pass.js';
import { AuditLogEntryType } from '../audit-log-entry-type.js';
import { AuditLogEntry } from '../audit-log-entry.js';

export class OpenAccessPassModifiedMaxCountEntry extends AuditLogEntry {
  static readonly TYPE_ID = '449edb0634144ba8ad23601f8b64efd0';

  private constructor(
    public readonly oldMaxCount: MigrationSafe<Cardinal>,
    public readonly newMaxCount: MigrationSafe<Cardinal>,
    public readonly before: OpenAccessPass,
    public readonly after: OpenAccessPass,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassModifiedMaxCountEntry.TYPE_ID;
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.EventOpenAccessPassModifiedMaxCount;
  }

  generateQueryData(): SerializedData {
    return {
      businessRef: this.after.businessRef.encode(),
      passId: this.after.uniqueId,
      orderId: this.after.orderId,
    };
  }

  static create(args: {
    oldMaxCount: MigrationSafe<Cardinal>,
    newMaxCount: MigrationSafe<Cardinal>,
    before: OpenAccessPass,
    after: OpenAccessPass,
  }): OpenAccessPassModifiedMaxCountEntry {
    return new OpenAccessPassModifiedMaxCountEntry(
        args.oldMaxCount,
        args.newMaxCount,
        args.before,
        args.after,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassModifiedMaxCountEntry {
    return this.create({
      oldMaxCount: MigrationSafe.decode(MapUtils.getRaw(map, 'oldMaxCount'), (data) => Cardinal.decode(JSONUtils.decodeNumber(data))),
      newMaxCount: MigrationSafe.decode(MapUtils.getRaw(map, 'newMaxCount'), (data) => Cardinal.decode(JSONUtils.decodeNumber(data))),
      before: context.fromData<OpenAccessPass>(MapUtils.getRecord(map, 'before')),
      after: context.fromData<OpenAccessPass>(MapUtils.getRecord(map, 'after')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      oldMaxCount: this.oldMaxCount.encode((value) => value.encode()),
      newMaxCount: this.newMaxCount.encode((value) => value.encode()),
      before: context.toData(this.before),
      after: context.toData(this.after),
    };
  }

  clone(): this {
    return OpenAccessPassModifiedMaxCountEntry.create({
      oldMaxCount: this.oldMaxCount.clone(cloneData),
      newMaxCount: this.newMaxCount.clone(cloneData),
      before: cloneData(this.before),
      after: cloneData(this.after),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.oldMaxCount.isSame(this.oldMaxCount, isSameData) &&
      other.newMaxCount.isSame(this.newMaxCount, isSameData) &&
      isSameData(other.before, this.before) &&
      isSameData(other.after, this.after);
  }
}

export class OpenAccessPassModifiedMaxCountEntryBuilder extends DataModelBuilder<OpenAccessPassModifiedMaxCountEntry> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassModifiedMaxCountEntry {
    return OpenAccessPassModifiedMaxCountEntry.fromMap(context, map);
  }
}
